const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
export type AcquisitionAttribution = Partial<Record<(typeof UTM_KEYS)[number], string>>;
const STORAGE_KEY = "eva_acquisition";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
let memory: { capturedAt: number; marketing: AcquisitionAttribution } | undefined;

function sanitize(value: Record<string, unknown>): AcquisitionAttribution {
  const result: AcquisitionAttribution = {};
  for (const key of UTM_KEYS) {
    const raw = value[key];
    if (typeof raw === "string") {
      const clean = raw
        .trim()
        .replace(/[\u0000-\u001f\u007f]/g, "")
        .slice(0, 200);
      if (clean) result[key] = clean;
    }
  }
  return result;
}

/** Only explicit campaign labels; never store URLs, referrers or advertising identifiers. */
export function parseAcquisition(search: string, hash: string): AcquisitionAttribution | undefined {
  const query = new URLSearchParams(search);
  const hashQuery = new URLSearchParams(hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "");
  const values: Record<string, unknown> = {};
  for (const key of UTM_KEYS) values[key] = hashQuery.get(key) || query.get(key);
  const marketing = sanitize(values);
  return marketing.utm_source ? marketing : undefined;
}

export function getAcquisition(): AcquisitionAttribution | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw) memory = JSON.parse(raw);
  } catch {
    // Storage may be blocked; keep the in-memory attribution for this page.
  }
  if (
    !memory ||
    !Number.isFinite(memory.capturedAt) ||
    Date.now() - memory.capturedAt >= MAX_AGE_MS ||
    memory.capturedAt > Date.now()
  ) {
    memory = undefined;
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
    return undefined;
  }
  const marketing = sanitize(memory.marketing || {});
  return marketing.utm_source ? marketing : undefined;
}

/** Capture before Angular redirects can remove the incoming query parameters. First touch wins. */
export function captureAcquisition(search?: string, hash?: string): void {
  if (typeof window === "undefined" || getAcquisition()) return;
  const marketing = parseAcquisition(search ?? window.location.search, hash ?? window.location.hash);
  if (!marketing) return;
  memory = { capturedAt: Date.now(), marketing };
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
  } catch {}
}
